import { Range } from 'rc-slider';
import React, { useEffect, useRef, useState } from 'react';

import { getCurrencySign } from '@travel/i18n';
import { TextField } from '@travel/ui';
import { cx } from '@travel/utils';

import debounce from '../../utils/debounce';

import 'rc-slider/assets/index.css';
import styles from './priceSlider.module.scss';

import type { RangeProps } from 'rc-slider';
/********************* START*************************************************
 * NOTE: rc-slider library currently accepts only styles object not classnames
 * **************************************************************************/
const railTStyle = { backgroundColor: '#cccccc', height: 2, borderRadius: 3 };
export const trackTStyle = {
  height: 2,
  borderRadius: 3,
  backgroundColor: '#54822B',
};
const handleTStyle = {
  width: 22,
  height: 22,
  borderRadius: '50%',
  top: 0,
  backgroundColor: '#54822B',
  border: '0',
};
/********************* END*************************************************/

type Props = {
  className?: string;

  /** Maximum range value of slider */
  maxRange: number;

  /** Minimum range value of slider */
  minRange: number;

  /** Setting left slider knob value */
  leftKnob: number;

  /** Setting right slider knob value */
  rightKnob: number;

  /** Steps count per move of the slider */
  step?: number;

  /** String to define the current selected currency */
  currency: string;

  /** Flag to define whether the currency will be a global one or not */
  isGlobalCurrency?: boolean;

  /** Function to passed back the latest slider knobs' value. */
  onChange?: (currentRange: number[]) => void;

  /** Function to passed back the input value on min text field */
  onLeftKnobChange?: (leftKnob: number) => void;

  /** Function to passed back the input value on max text field */
  onRightKnobChange?: (rightValue: number) => void;

  /** should hide clear buttons in price input*/
  shouldHideClear?: boolean;

  /** Function to define how the displayed price should be */
  pricePatternFunc?: (value: number | string, currencySymbol: string) => string;

  /** Function to define how price pattern will be validated by RegExp */
  priceRegexPatternFunc?: (currencySymbol: string) => string;

  /** Object containing custom Range component props */
  rangeProps?: RangeProps;
};

function PriceSlider(props: Props) {
  const isMinInputChangedRef = useRef(false);
  const isMaxInputChangedRef = useRef(false);

  const [minValue, setMinValue] = useState<string | number>(props.leftKnob);
  const [maxValue, setMaxValue] = useState<string | number>(props.rightKnob);
  const [isDebouncing, setIsDebouncing] = useState(false);

  const isMinValueNumber = typeof minValue === 'number';
  const isMaxValueNumber = typeof maxValue === 'number';
  const processedMaxValue = isMaxValueNumber ? Math.floor(Number(maxValue)) : maxValue;
  const processedMinValue = isMinValueNumber ? Math.floor(Number(minValue)) : minValue;

  useEffect(() => {
    setMinValue(props.leftKnob);
    setMaxValue(props.rightKnob);
  }, [props.leftKnob, props.rightKnob]);

  const currencySymbol = getCurrencySign(props.currency, props.isGlobalCurrency);
  const escapedSymbol = currencySymbol.replace('$', '\\$');
  const currencySymbolRegExp = new RegExp(escapedSymbol, 'g');
  const priceRegExp = props.priceRegexPatternFunc
    ? new RegExp(props.priceRegexPatternFunc(currencySymbol))
    : new RegExp(`^${escapedSymbol}([1-9][0-9]*|0)?$`);
  const emptyPrice = new RegExp(`^${escapedSymbol}$`);

  const onChange = (values: number[]) => {
    if (!isDebouncing) {
      props.onChange?.(values);
    }
  };

  const resetMinKnob = () => {
    props.onChange?.([props.minRange, props.rightKnob]);
  };

  const resetMaxKnob = () => {
    props.onChange?.([props.leftKnob, props.maxRange]);
  };

  const onChangeMinNumber = debounce<(event: React.ChangeEvent<HTMLInputElement>) => void>(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsDebouncing(false);

      let value = props.minRange;

      if (event.target.value.match(priceRegExp)) {
        const normalizedValue = Number(event.target.value.replace(currencySymbolRegExp, ''));
        // min range <= x <= max value
        value = Math.min(Number(maxValue), Math.max(normalizedValue, props.minRange));
      } else if (event.target.value.match(emptyPrice)) {
        value = props.minRange;
      }

      props.onLeftKnobChange?.(value);
      setMinValue(value);
    },
    100,
  );

  const onChangeMaxNumber = debounce((event: React.ChangeEvent<HTMLInputElement>) => {
    setIsDebouncing(false);

    let value = props.maxRange;

    if (event.target.value.match(priceRegExp)) {
      const normalizedValue = Number(event.target.value.replace(currencySymbolRegExp, ''));
      // min value <= x <= max range
      value = Math.min(props.maxRange, Math.max(normalizedValue, Number(minValue)));
    }

    props.onRightKnobChange?.(value);
    setMaxValue(value);
  }, 100);

  return (
    <div
      data-testid="priceSlider-wrapper"
      className={cx(props.className, styles.priceSliderWrapper)}
    >
      <div className={styles.sliderWrapper}>
        <Range
          // TODO: (A11Y) older version of rc-slider that doesn't support aria labels
          className=""
          allowCross={false}
          value={[props.leftKnob, props.rightKnob]}
          max={Math.floor(props.maxRange)}
          min={Math.floor(props.minRange)}
          step={props.step}
          trackStyle={[trackTStyle]}
          handleStyle={[handleTStyle, handleTStyle]}
          railStyle={railTStyle}
          onChange={onChange}
          {...props.rangeProps}
        />
      </div>
      <div className={styles.rangeInput}>
        <TextField
          value={
            props.pricePatternFunc
              ? props.pricePatternFunc(processedMinValue, currencySymbol)
              : `${currencySymbol}${processedMinValue}`
          }
          data-testid="textField-priceFrom-input"
          onClear={props.shouldHideClear ? undefined : resetMinKnob}
          shouldHideClearButtonOnBlur={props.shouldHideClear ? false : true}
          className={styles.textField}
          onBlur={(event: React.ChangeEvent<HTMLInputElement>) => {
            if (!isMinInputChangedRef.current) return null;

            props.onLeftKnobChange?.(Number(minValue));
            onChangeMinNumber(event);

            isMinInputChangedRef.current = false;
          }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setIsDebouncing(true);
            isMinInputChangedRef.current = true;

            if (event.target.value.length < currencySymbol.length) {
              const newCurrencySymbol = currencySymbol.substring(0, event.target.value.length);
              const escapedSymbol = newCurrencySymbol.replace('$', '\\$');
              const currencySymbolRegExp = new RegExp(escapedSymbol, 'g');
              setMinValue(event.target.value.replace(currencySymbolRegExp, ''));
            } else {
              const matchResult = event.target.value.match(priceRegExp)?.input;
              if (!matchResult) {
                event.preventDefault();
              } else {
                setMinValue(event.target.value.replace(currencySymbolRegExp, ''));
              }
            }
          }}
          inputProps={{ inputMode: 'numeric' }}
        />
        <span className={styles.minus}>-</span>
        <TextField
          value={
            props.pricePatternFunc
              ? props.pricePatternFunc(processedMaxValue, currencySymbol)
              : `${currencySymbol}${processedMaxValue}`
          }
          data-testid="textField-priceTo-input"
          onClear={props.shouldHideClear ? undefined : resetMaxKnob}
          shouldHideClearButtonOnBlur={props.shouldHideClear ? false : true}
          className={styles.textField}
          onBlur={(event: React.ChangeEvent<HTMLInputElement>) => {
            if (!isMaxInputChangedRef.current) return null;

            props.onRightKnobChange?.(Number(maxValue));
            onChangeMaxNumber(event);

            isMaxInputChangedRef.current = false;
          }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setIsDebouncing(true);
            isMaxInputChangedRef.current = true;

            if (event.target.value.length < currencySymbol.length) {
              const newCurrencySymbol = currencySymbol.substring(0, event.target.value.length);
              const escapedSymbol = newCurrencySymbol.replace('$', '\\$');
              const currencySymbolRegExp = new RegExp(escapedSymbol, 'g');
              setMaxValue(event.target.value.replace(currencySymbolRegExp, ''));
            } else {
              const matchResult = event.target.value.match(priceRegExp)?.input;
              if (!matchResult) {
                event.preventDefault();
              } else {
                setMaxValue(event.target.value.replace(currencySymbolRegExp, ''));
              }
            }
          }}
          inputProps={{ inputMode: 'numeric' }}
        />
      </div>
    </div>
  );
}

PriceSlider.defaultProps = {
  maxRange: 100,
  minRange: 0,
  step: 1,
};

export default PriceSlider;
